import React, {
  createRef,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import NewProjectContext from "../../../contexts/NewProjectContext";
import Button from "../../common/Button/index.jsx";
import { ReactComponent as ArrowRightSvg } from "../../../assets/icons/arrow-right.svg";
import { ReactComponent as ArrowLeftSvg } from "../../../assets/icons/arrow-left.svg";
import { ReactComponent as TrashSvg } from "../../../assets/admin/icons/trash.svg";
import AppContext from "../../../AppContext.jsx";
import { langComponent } from "../../../constants/index.jsx";

import programSketchUpImage from "../../../assets/icons/programs/sketchUp.png";
import program3dsMaxImage from "../../../assets/icons/programs/3dsMax.png";
import programRevitImage from "../../../assets/icons/programs/autodeskRevit.png";
import programCinema4DImage from "../../../assets/icons/programs/cinema4d.png";
import programUnrealImage from "../../../assets/icons/programs/unreal.png";
import programRhinoImage from "../../../assets/icons/programs/rhino.png";
import programAutocadImage from "../../../assets/icons/programs/autocad.png";
import programArchicadImage from "../../../assets/icons/programs/archicad.png";

import programVRayImage from "../../../assets/icons/programs/vray.png";
import programCoronaImage from "../../../assets/icons/programs/corona.png";
import programD5RenderImage from "../../../assets/icons/programs/d5render.png";
import programEnscapeImage from "../../../assets/icons/programs/enscape.jpg";
import programLumionImage from "../../../assets/icons/programs/lumion.png";

import TextArea from "../../common/TextArea/index.jsx";
import ImageUploadBox from "../../common/ImageUploadBox/index.jsx";
import TextBox from "../../common/TextBox/index.jsx";
import ComboBox from "../../common/ComboBox/index.jsx";
import ProjectFile from "./ProjectFile.jsx";
import { useScreenSize } from "../../index.js";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { TagsInput } from "react-tag-input-component";
import _ from "lodash";
import ImageCropperEx from "../../common/ImageCropperEx";

const MATERIAL_REQUIREMENT = {
  NOT_REQUIRED: 0,
  REQUIRED: 1,
  OPTIONAL: 2,
};

const prefix = "new-project-work-exchange-description";

const Description = () => {
  const { details, setDetails, goForward, goBackward, edit } =
    useContext(NewProjectContext);
  const {
    getLang,
    designStyles: {
      workExchanges: workExchangeTypes,
      // workExchangeDrawingTypes,
      // workExchangeMaterialTypes,
      // workExchangeModelTypes,
    },
  } = useContext(AppContext);
  const Translation = getLang(langComponent.workExchange);
  const TranslationCommon = getLang(langComponent.common);
  const swiperRef = useRef();
  const galleryRef = createRef();
  const formikRef = useRef();
  const { screenSize } = useScreenSize();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slider, setSlider] = useState(3);
  const [cover, setCover] = useState(details?.workExchange?.cover ?? undefined);
  const [images, setImages] = useState(details?.workExchange?.images || []);
  const [files, setFiles] = useState(details?.workExchange?.files || []);
  const [prevType, setPrevType] = useState(
    edit ? details?.workExchange?.info?.type : details?.workType
  );
  const [info] = useState(
    details?.workExchange?.info ?? {
      name: "",
      desc: "",
      fileTypes: [],
      program: undefined,
      renderer: undefined,
      category: undefined,
      subCategory: undefined,
      materials: [],
      price: 0,
      discount: 0,
      type: prevType,
    }
  );

  const _config = useMemo(() => {
    if (!prevType || !workExchangeTypes) return [];

    const _type = workExchangeTypes.find((t) => t.value === prevType);

    if (!_type)
      return {
        categories: [],
        fileTypes: [],
        requireProgram: true,
        requireRenderer: true,
        requireMaterials: MATERIAL_REQUIREMENT.NOT_REQUIRED,
      };

    return {
      categories: _type.categories.map((cat) => ({
        name: `${cat.name}${cat.projects ? ` (${cat.projects})` : ""}`,
        value: cat._id,
        subs: cat.subs.map((subcat) => ({
          name: `${subcat.name}${
            subcat.projects ? ` (${subcat.projects})` : ""
          }`,
          value: subcat._id,
          materials: subcat.materials.map((m) => ({
            name: `${m.name}${m.projects ? ` (${m.projects})` : ""}`,
            value: m._id,
          })),
        })),
      })),
      fileTypes: _type.fileTypes.map((ft) => ({
        name: ft,
        value: ft,
      })),
      requireProgram: _type.requireProgram,
      requireRenderer: _type.requireRenderer,
      requireMaterials: _type.requireMaterials,
    };
  }, [workExchangeTypes, prevType]);

  const [keywords, setKeywords] = useState(
    details?.workExchange?.info?.keywords ?? []
  );

  // * ===== Effects and Functions =====
  useEffect(() => {
    if (["md", "lg", "xl"].includes(screenSize)) {
      setSlider(3);
      return;
    }

    setSlider(2);
  }, [screenSize]);

  const handleGoBack = () => {
    goBackward();
  };

  const handleCropped = async (_file) => {
    const { blob, cropped, file, dataUrl, croppedDataUrl } = _file;

    setCover({
      original: file,
      cropped: blob ?? new Blob([file], { type: file.type }),
      dimension: cropped,
      dataUrl: croppedDataUrl ?? dataUrl,
    });
  };

  const handleUploadGalleryImage = (files) => {
    if (files.length === 0) return;

    const _files = [];
    const existingFileNames = images.map((image) => image.path.name);

    for (const file of [...files]) {
      // * Remove duplicate images
      if (existingFileNames.includes(file.name)) {
        continue;
      }

      _files.push({ path: file });
    }

    setImages([...images, ..._files]);
  };

  const handleProjectFile = (files) => {
    setFiles(files);
  };

  const handleDeleteMainCrop = () => {
    setCover(undefined);
  };

  const handleRemoveGalleryImage = (_id, file) => {
    if (file instanceof File) {
      setImages((curr) => [...curr.filter((f) => f?.path?.name !== file.name)]);
    } else {
      setImages((curr) => [...curr.filter((f) => f._id !== _id)]);

      const deleteList = details?.deleteImage || [];
      if (!deleteList.includes(_id)) {
        deleteList.push(_id);
        deleteList.push(_id);
        setDetails((curr) => ({
          ...curr,
          deleteImage: deleteList,
        }));
      }
    }
  };

  const handleValidate = (values) => {
    const {
      name,
      program,
      renderer,
      desc,
      fileTypes,
      category,
      subCategory,
      materials,
      type,
      price,
      discount,
    } = values;

    let errors = {};
    if (!name) errors.name = Translation("Please name your project");
    if (!fileTypes || fileTypes.length == 0)
      errors.workFileTypes = Translation("Please select type of work file");

    if (!desc) errors.desc = Translation("Please input Main description");

    if (discount > price)
      errors.discount = Translation("Discount can't exceed price");

    if (!category) errors.category = Translation("Please select a category");

    if (!subCategory)
      errors.subCategory = Translation("Please select a sub category");

    // * Check if need to validate program and renderer
    const { requireProgram, requireRenderer, requireMaterials } = _config;

    if (requireProgram && !program)
      errors.program = Translation("Please select the program used");
    if (requireRenderer && !renderer)
      errors.renderer = Translation("Please select the renderer used");
    if (
      requireMaterials === MATERIAL_REQUIREMENT.REQUIRED &&
      (!materials || materials.length === 0)
    ) {
      errors.materials = Translation("Please select atleast one material");
    }

    // * Check if type change to clear categories

    return errors;
  };

  const handleSubmit = (values) => {
    setDetails((curr) => ({
      ...curr,
      workExchange: {
        info: { ...values, keywords },
        cover,
        images,
        files,
      },
    }));

    goForward();
  };

  const handleImageGalleryDragEnd = (result) => {
    const originalIndex = result.source.index;
    const newIndex = result.destination.index;

    // * Swap panorama images
    const _images = _.cloneDeep(images);
    const _draggedImage = _images.splice(originalIndex, 1);
    _images.splice(newIndex, 0, ..._draggedImage);

    setImages(_images);
  };

  return (
    <Formik
      initialValues={info}
      validate={handleValidate}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {({ values, isValid, setFieldValue, setFieldTouched }) => {
        const _isValid =
          isValid && files.length > 0 && !_.isNil(cover) && images.length > 0;

        const _subOptions = values.category
          ? _config.categories.find((c) => c.value === values.category)?.subs
          : [];

        const _materials =
          values.category && values.subCategory
            ? _config.categories
                .find((c) => c.value === values.category)
                ?.subs.find((s) => s.value === values.subCategory)?.materials
            : [];

        useEffect(() => {
          // console.log("type", values?.type);

          if (prevType === values?.type) return;

          setFieldValue("category", undefined);
          setFieldTouched("category", false);
          setFieldValue("subCategory", undefined);
          setFieldTouched("subCategory", false);
          setFieldValue("materials", []);
          setFieldTouched("materials", false);
          setFieldValue("fileTypes", []);
          setFieldTouched("fileTypes", false);
          setFieldValue("program", undefined);
          setFieldTouched("program", false);
          setFieldValue("renderer", undefined);
          setFieldTouched("renderer", false);

          setPrevType(values.type);
        }, [values?.type]);

        return (
          <Form className={prefix}>
            <div className={`${prefix}-content`}>
              <div className={`${prefix}-content-main`}>
                <div className={`${prefix}-content-main-description`}>
                  <TextBox
                    name="name"
                    label={Translation("Project Name")}
                    placeholder={Translation("Project Name")}
                    disabled={false}
                    maxLength={50}
                  />
                  {edit && (
                    <ComboBox
                      label={Translation("Type")}
                      name="type"
                      options={workExchangeTypes}
                    />
                  )}
                  <ComboBox
                    label={Translation("Work File Types")}
                    name="fileTypes"
                    placeholder={Translation("File Types")}
                    multi={true}
                    options={_config.fileTypes}
                  />
                  {_config.requireProgram && _config.requireRenderer && (
                    <div
                      className={`${prefix}-content-main-description-programs`}
                    >
                      <ComboBox
                        label={Translation("Program Used")}
                        name="program"
                        width={"100%"}
                        options={[
                          {
                            name: "SketchUp",
                            value: "sketchUp",
                            icon: programSketchUpImage,
                          },
                          {
                            name: "3ds Max",
                            value: "3dsMax",
                            icon: program3dsMaxImage,
                          },
                          {
                            name: "Autodesk Revit",
                            value: "revit",
                            icon: programRevitImage,
                          },
                          {
                            name: "Cinema 4D",
                            value: "cinema4D",
                            icon: programCinema4DImage,
                          },
                          {
                            name: "Unreal",
                            value: "unreal",
                            icon: programUnrealImage,
                          },
                          {
                            name: "Rhinocerus",
                            value: "rhino",
                            icon: programRhinoImage,
                          },
                          {
                            name: "Autocad",
                            value: "autocad",
                            icon: programAutocadImage,
                          },
                          {
                            name: "Archicad",
                            value: "archicad",
                            icon: programArchicadImage,
                          },
                        ]}
                        placeholder={Translation("Program Used")}
                      />
                      <ComboBox
                        name="renderer"
                        label={Translation("Renderer")}
                        width={"100%"}
                        options={[
                          {
                            name: "V-Ray",
                            value: "vray",
                            icon: programVRayImage,
                          },
                          {
                            name: "Corona",
                            value: "corona",
                            icon: programCoronaImage,
                          },
                          {
                            name: "Lumion",
                            value: "lumion",
                            icon: programLumionImage,
                          },
                          {
                            name: "Enscape",
                            value: "enscape",
                            icon: programEnscapeImage,
                          },
                          {
                            name: "D5render",
                            value: "d5render",
                            icon: programD5RenderImage,
                          },
                        ]}
                        placeholder={`${Translation("Renderer")}`}
                      />
                    </div>
                  )}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, minmax(auto, 1fr))",
                      columnGap: 18,
                    }}
                  >
                    <ComboBox
                      label={Translation("Category")}
                      name="category"
                      placeholder={Translation("Category")}
                      options={_config.categories}
                    />
                    <ComboBox
                      label={Translation("Sub Category")}
                      name="subCategory"
                      placeholder={
                        values.category
                          ? Translation("Sub Category")
                          : Translation("Select category first")
                      }
                      options={_subOptions}
                      disabled={values.category ? false : true}
                    />
                  </div>
                  {_config.requireMaterials !==
                    MATERIAL_REQUIREMENT.NOT_REQUIRED && (
                    <ComboBox
                      label={Translation(
                        `Materials ${
                          _config.requireMaterials ===
                          MATERIAL_REQUIREMENT.OPTIONAL
                            ? "(Optional)"
                            : ""
                        }`
                      )}
                      name="materials"
                      placeholder={
                        values.subCategory
                          ? Translation("Select materials")
                          : Translation("Select sub category first")
                      }
                      options={_materials}
                      multi={true}
                      disabled={values.subCategory ? false : true}
                    />
                  )}
                  <TextArea
                    name={"desc"}
                    label={Translation("Description")}
                    placeholder={Translation("Description")}
                    maxLength={150}
                  />
                  <div
                    style={{
                      display: "grid",
                      columnGap: 18,
                      gridTemplateColumns: "repeat(2, minmax(auto, 1fr))",
                    }}
                  >
                    <TextBox
                      name="price"
                      label={Translation("Price (Optional)")}
                      placeholder={Translation("Price")}
                      type="number"
                    />
                    <TextBox
                      name="discount"
                      label={Translation("Discount (Optional)")}
                      placeholder={Translation("Discount")}
                      type="number"
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        fontSize: 14,
                        color: "#333",
                        margin: "0 8px",
                      }}
                    >
                      Keywords (Limit 10)
                    </label>
                    <TagsInput
                      id={"work-exchange-tags"}
                      value={keywords}
                      onChange={setKeywords}
                      beforeAddValidate={(tag, existingTags) => {
                        if (existingTags.length + 1 > 10) {
                          return false;
                        } else {
                          return true;
                        }
                      }}
                      name="Tag"
                      placeHolder="Keywords"
                    />
                    <label
                      style={{
                        fontSize: 12,
                        color: "#5c5c5c",
                        margin: "4px 8px",
                        opacity: 0.5,
                      }}
                    >
                      Enter keywords to help other find your projects
                    </label>
                  </div>
                </div>
                <div className={`${prefix}-content-main__image`}>
                  <ImageCropperEx
                    name={"main"}
                    aspectRatio={0.8}
                    multiple={false}
                    onChange={(images) => {
                      if (!images || images.length === 0) {
                        handleDeleteMainCrop();
                        return;
                      }

                      handleCropped(images[0]);
                    }}
                    defaultImages={cover ? [cover] : undefined}
                  />
                </div>
              </div>
              <div className={`${prefix}-content-gallery`}>
                <div className={`${prefix}-content-gallery-action__section`}>
                  <div className={`${prefix}-content-gallery-action__header`}>
                    {Translation("Gallery Image")}
                  </div>
                </div>
                <div className={`${prefix}-content-gallery-scroller`}>
                  <DragDropContext onDragEnd={handleImageGalleryDragEnd}>
                    <Droppable droppableId="panoramas" direction="horizontal">
                      {(provided) => (
                        <div
                          className={`${prefix}-content-gallery-list`}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          <label
                            className={`${prefix}-content-gallery-list__item`}
                          >
                            <ImageUploadBox
                              name="gallery"
                              ref={galleryRef}
                              onChange={handleUploadGalleryImage}
                              multiple={true}
                            />
                          </label>
                          {[...images].map(({ _id, path }, index) => {
                            let _src = path;

                            if (typeof path !== "string") {
                              _src = URL.createObjectURL(path);
                            }

                            return (
                              <Draggable
                                draggableId={index.toString()}
                                index={index}
                                key={`draggable-${index}`}
                              >
                                {(provided) => (
                                  <div
                                    key={`gallery-image-key-${index}`}
                                    className={`${prefix}-content-gallery-list__item`}
                                    ref={provided.innerRef}
                                    {...provided.dragHandleProps}
                                    {...provided.draggableProps}
                                  >
                                    <img
                                      id={`gallery-image-${index}`}
                                      className={`${prefix}-content-gallery-list__item__image`}
                                      alt={`gallery-image${index}`}
                                      src={_src}
                                    />
                                    <TrashSvg
                                      className="new-project-image-upload-gallery-image__trash"
                                      onClick={() =>
                                        handleRemoveGalleryImage(_id, path)
                                      }
                                    />
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
                {/* <div className={`${prefix}-content-gallery-list`}>
                  <Swiper
                    ref={swiperRef}
                    modules={[Navigation]}
                    slidesPerView={slider}
                    spaceBetween={10}
                    loop={false}
                    navigation={false}
                    onSlideChange={(swiperCore) => {
                      const { activeIndex } = swiperCore;
                      setCurrentSlide(activeIndex);
                    }}
                  >
                    <SwiperSlide
                      className={`${prefix}-content-gallery-list__item`}
                    >
                      <ImageUploadBox
                        ref={galleryRef}
                        handleFileChange={handleUploadGalleryImage}
                        multiple={true}
                      />
                    </SwiperSlide>
                    {[...images].map(({ _id, path }, index) => {
                      const _src =
                        typeof path === "string"
                          ? path
                          : URL.createObjectURL(path);
                      return (
                        <SwiperSlide
                          key={`gallery-image-key-${index}`}
                          className={`${prefix}-content-gallery-list__item`}
                        >
                          <img
                            id={`gallery-image-${index}`}
                            alt={`gallery-image${index}`}
                            src={_src}
                          />
                          <TrashSvg
                            className="new-project-image-upload-gallery-image__trash"
                            onClick={() => handleRemoveGalleryImage(_id, path)}
                          />
                        </SwiperSlide>
                      );
                    })}
                  </Swiper>
                </div> */}
              </div>
              <div className={`${prefix}-content-file`}>
                <div className={`${prefix}__header`}>
                  {Translation("Work Files")}
                </div>
                <div className={`${prefix}-content-file__input`}>
                  <ProjectFile handleFileChange={handleProjectFile} />
                </div>
              </div>
            </div>
            <div className={`${prefix}-footer`}>
              <div className={`new-project-work-exchange__footer`}>
                <Button
                  label={TranslationCommon("Return")}
                  variant={"secondary"}
                  leadingIcon={<ArrowLeftSvg />}
                  onClick={() => handleGoBack()}
                />
                <Button
                  label={TranslationCommon("Continue")}
                  type="submit"
                  trailingIcon={<ArrowRightSvg />}
                  disabled={!_isValid}
                />
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

Description.propTypes = {
  _id: PropTypes.string,
};

Description.defaultProps = {
  _id: undefined,
};

export default Description;
